<template>
    <div class="data-table-container">

        <span v-if="!props.model || !props.model.length">
            <em>Required model is missing.</em>
        </span>

      <!-- Filters 
      TODO: Add Filter Slots
      
      
      -->
        
      
        <!-- Column visibility dropdown and custom filters -->
        <div v-if="localMeta.showHeader" class="mb-3 container-fluid">
          <div class="row">
            <div class="col-auto px-0">
              <div class="column-selector" ref="columnSelectorRef">
                <button 
                  type="button"
                  class="column-selector-trigger"
                  @click="toggleColumnDropdown"
                >
                  <i class="fas fa-filter me-1"></i>
                  <span class="trigger-label">Choose Columns</span>
                  <i class="fas fa-chevron-down ms-1"></i>
                </button>
                
                <div v-if="columnDropdownVisible" class="column-dropdown dropdown-menu show" @click.stop>
                  <div class="dropdown-item column-select-all-header">
                    <label class="d-flex align-items-center mb-0 fw-bold">
                      <input 
                        type="checkbox" 
                        class="form-check-input me-2"
                        :checked="allColumnsSelected"
                        @change="toggleAllColumns" 
                      />
                      <span>{{ allColumnsSelected ? 'Deselect All' : 'Select All' }}</span>
                    </label>
                  </div>
                  <div class="dropdown-divider"></div>
                  <div v-for="col in model" :key="col.name" class="dropdown-item">
                    <label class="d-flex align-items-center mb-0">
                      <input 
                        type="checkbox" 
                        class="form-check-input me-2"
                        v-model="visibleColumns" 
                        :value="col.name"
                        @change="updateVisibleColumns" 
                      />
                      <span>{{ col.label || col.name || 'Not Provided' }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <!-- Before search slot -->
            <div class="col-auto px-0 ms-3">
              <slot name="before-search" :filter="localFilter" :updateFilter="updateFilter"></slot>
            </div>
            <!-- Custom filters slot -->
            <div class="col-auto px-0 ms-3">
              <slot name="filters" :filter="localFilter" :updateFilter="updateFilter"></slot>
            </div>
            <!-- Search bar (conditionally rendered) -->
            <div v-if="!hideSearch" class="col px-0 ms-3" :class="{ 'me-3': slots['after-search'] }">
              <div class="input-group input-group-sm">
                <input 
                    type="text" 
                    class="form-control" 
                    placeholder="Search..." 
                    v-model="localFilter.search" 
                    @input="handleSearchInput"
                />
              </div>
            </div>
            <!-- After search slot -->
            <div class="col-auto px-0 ms-auto" v-if="slots['after-search']">
              <slot name="after-search" :filter="localFilter" :updateFilter="updateFilter"></slot>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="table-responsive" ref="tableContainer">
            <table :class="['table table-hover', localMeta.tableClass]">
                <thead v-if="localMeta.showHeader">
                    <tr :style="localMeta.rowStyle" :class="localMeta.rowClass">
                        <th v-for="col in visibleModel" :key="col.name"
                            :style="[localMeta.colStyle, { width: columnWidths[col.name + ''] + 'px' }]"
                            :class="[
                                localMeta.colClass, 
                                'position-relative', 
                                'draggable-column',
                                { 'drag-over': dragOverColumn === col.name },
                                { 'dragging': draggedColumn === col.name }
                            ]"
                            draggable="true"
                            @dragstart="handleDragStart($event, col.name || '')"
                            @dragover="handleDragOver($event, col.name || '')"
                            @dragleave="handleDragLeave"
                            @drop="handleDrop($event, col.name || '')"
                            @dragend="handleDragEnd">
                            <div class="d-flex align-items-center">
                                <span class="drag-handle me-2">
                                    <i class="fas fa-grip-vertical"></i>
                                </span>
                                <span>{{ col.label || col.name || 'Not Provided' }}</span>
                                <span v-if="col.sortable" class="ms-2 cursor-pointer" @click="toggleSort(col.name)">
                                    <i :class="getSortIcon(col.name)"></i>
                                </span>
                                <div class="resizer" @mousedown.stop="startResize($event, col.name)"></div>
                            </div>
                        </th>
                    </tr>
                </thead>
                
                <!-- Under header slot for additional content like filter badges -->
                <tbody v-if="$slots['under-header']" class="under-header-section">
                    <tr>
                        <td :colspan="visibleModel.length" class="p-2 bg-light border-bottom">
                            <slot name="under-header"></slot>
                        </td>
                    </tr>
                </tbody>
                
                <tbody>
                    <tr v-for="(row, index) in paginatedData" :key="index"
                        :class="[getRowClass(row), { 'table-active': selectedRow === index }]" :style="localMeta.rowStyle"
                        @click="selectRow(index)">
                        <td v-for="col in visibleModel" :key="col.name"
                            :class="[localMeta.colClass, { 'position-sticky': col.locked, 'start-0': col.locked }]"
                            :style="localMeta.colStyle">

                            <slot 
                                :name="`view_${col.type || 'string'}`" 
                                :row="row" 
                                :column="col" 
                                :value="row[col.name + '']"
                                :is-truncated="shouldTruncate(col, row[col.name + ''])"
                                :truncated-value="truncateText(row[col.name + ''], col)"
                                :show-full-text="(content: string) => showFullText(content, col.label || col.name || '')"
                            > 
                                <span v-if="shouldTruncate(col, row[col.name + ''])">
                                    {{ truncateText(row[col.name + ''], col) }}
                                    <button 
                                        class="btn btn-sm btn-link p-0 ms-1" 
                                        @click.stop="showFullText(row[col.name + ''], col.label || col.name || '')"
                                        title="View full text"
                                    >
                                        <i class="fas fa-search"></i>
                                    </button>
                                </span>
                                <component 
                                    v-else
                                    :is="getCellComponent(col.type)" 
                                    :value="row[col.name + '']" 
                                    :column="col" 
                                ></component>
                            </slot>
                        </td>
                    </tr>
                    
                    <!-- No data row -->
                    <tr v-if="paginatedData.length === 0" class="no-data-row">
                        <td :colspan="visibleModel.length" class="text-center py-4 text-muted">
                            <div class="d-flex flex-column align-items-center">
                                <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                                <span>No data available</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination Footer -->
        <div v-if="localMeta.showFooter" class="d-flex justify-content-between align-items-center mt-3">
            <div>
                <span>Showing page: </span>
                <select v-model="localFilter.page" @change="updateFilter" class="form-select d-inline-block w-auto">
                    <option 
                        v-for="page in (totalPages)" 
                        :key="page" 
                        :value="page - 1"
                    >{{ page }}</option>
                </select>
                <span> of {{ totalPages }}</span>
            </div>
            <div>
                <span>Size: </span>
                <select v-model="localFilter.size" @change="updateFilter" class="form-select d-inline-block w-auto">
                    <option v-for="size in [10, 25, 50, 100, 'All']" :key="size" :value="size">{{ size }}</option>
                </select>
            </div>
        </div>
    </div>
    
    <!-- Full Text Modal -->
    <kt-modal
        :visible="fullTextModal.visible"
        :title="fullTextModal.title"
        @dismiss="fullTextModal.visible = false"
    >
        <template #body>
            <div class="full-text-content" style="white-space: pre-wrap; word-wrap: break-word;">
                {{ fullTextModal.content }}
            </div>
        </template>
        <template #footer>
            <button class="btn btn-outline-secondary" @click="fullTextModal.visible = false">
                Close
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, defineComponent, type Component, type PropType, h, useAttrs, nextTick, getCurrentInstance, useSlots } from 'vue';
import { useRoute } from 'vue-router';
import KtDropmenu from './KtDropmenu.vue';
import KtModal from './KtModal.vue';
import type { KtDatatableModelProp, KtDatatableFilter, KtDatatableFilterSort, KtDatatableMeta, KtDatatableData } from './KtDataTable';

const slots = useSlots();

// Full text modal state
const fullTextModal = ref({
    visible: false,
    title: '',
    content: ''
});

// Props with defaults for optional properties
const props = defineProps({
    model: {
        type: Array as PropType<KtDatatableModelProp[]>,
        default: () => []
    },
    data: {
        type: Array as PropType<KtDatatableData>,
        default: () => []
    },
    filter: {
        type: Object as PropType<KtDatatableFilter>,
        default: () => ({})
    },
    meta: {
        type: Object as PropType<KtDatatableMeta>,
        default: () => ({})
    },
    multiSort: {
        type: Boolean,
        default: false
    },
    updateFunction: {
        type: Function,
        default: null
    },
    hideSearch: {
        type: Boolean,
        default: false
    },
    storageKey: {
        type: String,
        default: ''  // Will auto-generate if empty
    }
});

const emit = defineEmits<{
    (e: 'update', value: KtDatatableFilter): void;
}>();

const route = useRoute();

// Generate unique storage keys based on route and optional prop
const storageKeys = computed(() => {
    const baseKey = props.storageKey || route.path.replace(/\//g, '_').substring(1) || 'default';
    return {
        columnOrder: `dataTableColumnOrder_${baseKey}`,
        columnWidths: `dataTableColumnWidths_${baseKey}`
    };
});

// Check if server-side pagination is enabled via updateFunction prop
const hasUpdateFunction = computed(() => {
    const hasFunction = !!props.updateFunction;
    console.log('Has update function:', hasFunction);
    return hasFunction;
});

// Reactive state
const localFilter = ref<KtDatatableFilter>({
    page: props.filter?.page ?? 0,
    size: props.filter?.size ?? 25,
    total: props.filter?.total ?? 0,
    search: props.filter?.search ?? '',
    sort: props.filter?.sort ?? []
});

// Search debouncing
const searchTimeout = ref<NodeJS.Timeout | null>(null);
const debouncedSearch = ref<string>('');

const localMeta = ref<KtDatatableMeta>({
    showHeader: props.meta?.showHeader ?? true,
    showFooter: props.meta?.showFooter ?? true,
    rowStyle: props.meta?.rowStyle ?? "",
    rowClass: props.meta?.rowClass ?? "",
    colStyle: props.meta?.colStyle ?? "",
    colClass: props.meta?.colClass ?? "",
    tableClass: props.meta?.tableClass ?? "table-bordered",
    colDefaultMinWidth: props.meta?.colDefaultMinWidth ?? 150,
})

// Helper function to get row class - supports both string and function
const getRowClass = (row: any) => {
    if (typeof localMeta.value.rowClass === 'function') {
        return localMeta.value.rowClass(row);
    }
    return localMeta.value.rowClass || '';
};

// Truncation helper functions
const shouldTruncate = (col: KtDatatableModelProp, value: any): boolean => {
    if (!value || typeof value !== 'string') return false;
    if (!col.truncate && !col.truncateWords) return false;
    
    if (col.truncate && value.length > col.truncate) return true;
    if (col.truncateWords) {
        const words = value.trim().split(/\s+/);
        return words.length > col.truncateWords;
    }
    return false;
};

const truncateText = (value: string, col: KtDatatableModelProp): string => {
    if (!value) return '';
    
    if (col.truncate) {
        return value.length > col.truncate ? value.substring(0, col.truncate) + '...' : value;
    }
    
    if (col.truncateWords) {
        const words = value.trim().split(/\s+/);
        if (words.length > col.truncateWords) {
            return words.slice(0, col.truncateWords).join(' ') + '...';
        }
    }
    
    return value;
};

const showFullText = (content: string, title: string) => {
    fullTextModal.value = {
        visible: true,
        title: `Full Text: ${title}`,
        content: content || ''
    };
};

const visibleColumns = ref<string[]>(props.model?.filter(col => col.display ?? true).map(col => col.name ?? ''));
const columnWidths = ref<Record<string, number>>({});
const columnOrder = ref<string[]>([]);
const selectedRow = ref<number | null>(null);

// Column selector dropdown state
const columnSelectorRef = ref<HTMLElement | null>(null);
const columnDropdownVisible = ref(false);

const toggleColumnDropdown = () => {
  columnDropdownVisible.value = !columnDropdownVisible.value;
};

// Check if all columns are selected
const allColumnsSelected = computed(() => {
  const allColumnNames = props.model.map(col => col.name ?? '');
  return allColumnNames.length > 0 && allColumnNames.every(name => visibleColumns.value.includes(name));
});

// Toggle all columns on or off
const toggleAllColumns = () => {
  if (allColumnsSelected.value) {
    // Deselect all
    visibleColumns.value = [];
  } else {
    // Select all
    visibleColumns.value = props.model.map(col => col.name ?? '');
  }
  updateVisibleColumns();
};

// Click outside detection for column dropdown
const handleColumnClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  
  if (
    columnDropdownVisible.value &&
    columnSelectorRef.value &&
    !columnSelectorRef.value.contains(target)
  ) {
    columnDropdownVisible.value = false;
  }
};
const tableContainer = ref<HTMLElement | null>(null);

// Drag and drop state
const draggedColumn = ref<string | null>(null);
const dragOverColumn = ref<string | null>(null);

const columnsDrop = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const testRef = ref<HTMLElement | null>(null);


// Load persisted column widths and order
onMounted(() => {
    document.addEventListener('click', handleColumnClickOutside);
    
    const savedWidths = localStorage.getItem(storageKeys.value.columnWidths);
    if (savedWidths) {
        const parsedWidths = JSON.parse(savedWidths);
        const currentColumns = props.model.map(col => col.name ?? '');
        
        // Validate saved widths - remove obsolete columns and add missing ones
        const validatedWidths: Record<string, number> = {};
        let needsReset = false;
        
        currentColumns.forEach(colName => {
            if (parsedWidths[colName] !== undefined) {
                validatedWidths[colName] = parsedWidths[colName];
            } else {
                // New column, use default width
                validatedWidths[colName] = props.meta?.colDefaultMinWidth ?? 150;
                needsReset = true;
            }
        });
        
        // Check if any saved columns no longer exist
        Object.keys(parsedWidths).forEach(savedCol => {
            if (!currentColumns.includes(savedCol)) {
                needsReset = true;
            }
        });
        
        columnWidths.value = validatedWidths;
        
        // Update localStorage if columns changed
        if (needsReset) {
            console.warn('Column structure changed, updating saved column widths');
            localStorage.setItem(storageKeys.value.columnWidths, JSON.stringify(validatedWidths));
        }
    } else {
        props.model.forEach(col => {
            columnWidths.value[col.name ?? ''] = props.meta?.colDefaultMinWidth ?? 150;
        });
    }

    // Load persisted column order with validation
    const savedOrder = localStorage.getItem(storageKeys.value.columnOrder);
    if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);
        const currentColumns = props.model.map(col => col.name ?? '');
        
        // Validate saved order matches current columns
        const isValidOrder = parsedOrder.length === currentColumns.length && 
                            parsedOrder.every((col: string) => currentColumns.includes(col)) &&
                            currentColumns.every((col: string) => parsedOrder.includes(col));
        
        if (isValidOrder) {
            columnOrder.value = parsedOrder;
        } else {
            console.warn('Saved column order is invalid (columns changed), resetting to default order');
            // Reset localStorage and use default order
            localStorage.removeItem(storageKeys.value.columnOrder);
            columnOrder.value = currentColumns;
        }
    } else {
        // Initialize with default order from model
        columnOrder.value = props.model.map(col => col.name ?? '');
    }

    // document.addEventListener('click', handleOutsideClick);
});

// Save column widths on change
watch(columnWidths, (newWidths) => {
    localStorage.setItem(storageKeys.value.columnWidths, JSON.stringify(newWidths));
}, { deep: true });

// Save column order on change
watch(columnOrder, (newOrder) => {
    localStorage.setItem(storageKeys.value.columnOrder, JSON.stringify(newOrder));
}, { deep: true });

// Watch for filter prop changes and sync to localFilter
watch(() => props.filter, (newFilter) => {
    if (newFilter) {
        localFilter.value.page = newFilter.page ?? localFilter.value.page;
        localFilter.value.size = newFilter.size ?? localFilter.value.size;
        localFilter.value.total = newFilter.total ?? localFilter.value.total;
        localFilter.value.search = newFilter.search ?? localFilter.value.search;
        localFilter.value.sort = newFilter.sort ?? localFilter.value.sort;
    }
}, { deep: true, immediate: true });

// Computed properties
const visibleModel = computed<KtDatatableModelProp[]>(() => {
    // Filter visible columns and sort them according to columnOrder
    const visibleCols = props.model.filter(col => visibleColumns.value.includes(col.name ?? ''));
    
    // Sort by the order specified in columnOrder
    return visibleCols.sort((a, b) => {
        const aIndex = columnOrder.value.indexOf(a.name ?? '');
        const bIndex = columnOrder.value.indexOf(b.name ?? '');
        
        // If column not found in order, put it at the end
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
    });
});

const filteredData = computed(() => {
    // If we have an update function, we assume the data is already filtered by the server.
    if (hasUpdateFunction.value) {
        return props.data;
    }

    // Client-side filtering based on search
    if (!debouncedSearch.value) {
        return props.data;
    }

    const searchTerm = debouncedSearch.value.toLowerCase();
    return props.data.filter(row => {
        const rowString = JSON.stringify(row).toLowerCase();
        return rowString.includes(searchTerm);
    });
});

const sortedData = computed(() => {
    // If we have an update function, we assume the data is already sorted by the server.
    if (hasUpdateFunction.value || !localFilter.value.sort || localFilter.value.sort.length === 0) {
        return filteredData.value;
    }

    // Client-side sorting
    const sorted = [...filteredData.value].sort((a, b) => {
        for (const s of localFilter.value.sort || []) {
            const colName = Object.keys(s)[0];
            const dir = s[colName];
            if (a[colName] < b[colName]) return dir === 'ASC' ? -1 : 1;
            if (a[colName] > b[colName]) return dir === 'ASC' ? 1 : -1;
        }
        return 0;
    });

    return sorted;
});

const totalPages = computed<number>(() => {
    const size = parseInt(localFilter.value.size + "");
    if (size === 0 || localFilter.value.size === 'All') return 1;

    // Server-side: total is provided by the parent.
    if (hasUpdateFunction.value) {
        return Math.ceil((props.filter.total ?? 0) / size);
    }

    // Client-side: calculate total from data length.
    return Math.ceil(sortedData.value.length / size);
});

const paginatedData = computed<KtDatatableData>(() => {
    // Server-side: The parent is expected to provide the already-paginated data.
    if (hasUpdateFunction.value) {
        return sortedData.value;
    }

    // Client-side pagination
    if (localFilter.value.size === 'All') return sortedData.value;
    const start = (localFilter.value.page ?? 0) * parseInt(localFilter.value.size + "");
    return sortedData.value.slice(start, start + parseInt(localFilter.value.size + ""));
});

// Cell rendering components
const GuidCell = defineComponent({
    props: {
        value: { type: String, default: '' }
    },
    render() {
        return h('span', this.value);
    }
});

const StringCell = defineComponent({
    props: {
        value: { type: String, default: '' }
    },
    render() {
        return h('span', `${this.value}`);
    }
});

const NumberCell = defineComponent({
    props: {
        value: { type: Number, default: 0 }
    },
    render() {
        return h('span', this.value.toString());
    }
});

const DateCell = defineComponent({
    props: {
        value: { type: String, default: '' }
    },
    render() {
        if (!this.value) return h('span', '');
        
        // If date string has no time component, add one to ensure local interpretation
        // Without this, "2025-11-05" is treated as UTC midnight and converts to previous day local
        let dateValue = this.value;
        if (dateValue.indexOf('T') < 0 && dateValue.indexOf(' ') < 0) {
            dateValue = dateValue + ' 00:00:00';
        }
        
        // Display date in user's local timezone
        const date = new Date(dateValue);
        const formattedDate = date.toLocaleDateString();
        
        return h('span', formattedDate);
    }
});

const BooleanCell = defineComponent({
    props: {
        value: { type: Boolean, default: false }
    },
    render() {
        return h('span', (this.value ? "Yes" : "No"));
    }
});

const EmailCell = defineComponent({
    props: {
        value: { type: String, default: '' }
    },
    render() {
        return h('a', { href: `mailto:${this.value}` }, this.value);
    }
});

const SelectCell = defineComponent({
    props: {
        value: { type: [String, Number], default: '' },
        column: { type: Object as () => KtDatatableModelProp, required: true }
    },
    render() {
        const option = this.column.options?.find(opt => opt.value === this.value);
        const displayValue = option ? option.label : String(this.value);
        return h('span', displayValue);
    }
});

const ButtonCell = defineComponent({
    props: {
        value: { type: String, default: '' }
    },
    render() {
        return h('button', { class: 'btn btn-sm btn-outline-primary' }, this.value || 'Click');
    }
});

const ButtonGroupCell = defineComponent({
    render() {
        return h('div', { class: 'btn-group' }, [
            h('button', { class: 'btn btn-sm btn-outline-secondary' }, 'Action')
        ]);
    }
});

const CustomCell = defineComponent({
    props: {
        value: { type: [String, Number, Boolean], default: '' },
        column: { type: Object, default: () => ({}) }
    },
    render() {
        const { column, value } = this;
        // If column has a format function, use it; otherwise display raw value
        const displayValue = column.format ? column.format(value) : value;
        return h('span', displayValue);
    }
});

const cellComponents: Record<string, Component> = {
    guid: GuidCell,
    string: StringCell,
    number: NumberCell,
    date: DateCell,
    boolean: BooleanCell,
    email: EmailCell,
    select: SelectCell,
    button: ButtonCell,
    'button-group': ButtonGroupCell,
    custom: CustomCell
};

const getCellComponent = (type: string | undefined): Component => 
{
    return cellComponents[type ?? 'string'] || cellComponents.string;
}

// Sorting
const getSortIcon = (colName: string | undefined): string => {
    const sort = localFilter.value.sort?.find(s => Object.keys(s)[0] === colName);
    if (!sort || !colName) return 'fas fa-sort';
    return sort[colName] === 'ASC' ? 'fas fa-sort-up' : 'fas fa-sort-down';
};

const toggleSort = (colName: string | undefined): void => {
    if (!colName) return;
    const sort = localFilter.value.sort ? [...localFilter.value.sort] : [];
    const existing = sort.find(s => Object.keys(s)[0] === colName);
    
    if (props.multiSort) {
        // Multi-sort mode: Allow multiple columns to be sorted simultaneously
        if (existing) {
            if (existing[colName] === 'ASC') {
                existing[colName] = 'DESC';
            } else {
                const index = sort.indexOf(existing);
                sort.splice(index, 1);
            }
        } else {
            sort.push({ [colName]: 'ASC' });
        }
        localFilter.value.sort = sort;
    } else {
        // Single-sort mode: Reset all other sorts when clicking a column
        if (existing) {
            // If this column is already sorted, toggle between ASC -> DESC -> none
            if (existing[colName] === 'ASC') {
                // Reset all other sorts and set this column to DESC
                localFilter.value.sort = [{ [colName]: 'DESC' }];
            } else {
                // Remove sorting entirely
                localFilter.value.sort = [];
            }
        } else {
            // Reset all other sorts and set this column to ASC
            localFilter.value.sort = [{ [colName]: 'ASC' }];
        }
    }
    updateFilter();
};

// Column resizing
let resizingColumn: string | null = null;
let startX: number = 0;
let startWidth: number = 0;

const startResize = (event: MouseEvent, colName: string | undefined): void => {
    if (!colName) return;
    event.preventDefault(); // Prevent any default behavior
    event.stopPropagation(); // Stop event from bubbling
    resizingColumn = colName;
    startX = event.pageX;
    startWidth = columnWidths.value[colName] || (props.meta?.colDefaultMinWidth ?? 150);
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
};

const resize = (event: MouseEvent): void => {
    if (resizingColumn) {
        const delta = event.pageX - startX;
        const newWidth = Math.max(50, startWidth + delta); // Min width 50px
        columnWidths.value[resizingColumn] = newWidth;
    }
};

const stopResize = (): void => {
    resizingColumn = null;
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
};

// Column visibility
const updateVisibleColumns = (): void => {
    updateFilter();
};

// Row selection
const selectRow = (index: number): void => {
    selectedRow.value = index;
};

// Drag and drop functions
const handleDragStart = (event: DragEvent, colName: string): void => {
    draggedColumn.value = colName;
    if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', colName);
    }
};

const handleDragOver = (event: DragEvent, colName: string): void => {
    event.preventDefault();
    dragOverColumn.value = colName;
    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
    }
};

const handleDragLeave = (): void => {
    dragOverColumn.value = null;
};

const handleDrop = (event: DragEvent, targetColName: string): void => {
    event.preventDefault();
    
    if (!draggedColumn.value || draggedColumn.value === targetColName) {
        draggedColumn.value = null;
        dragOverColumn.value = null;
        return;
    }
    
    // Reorder columns
    const newOrder = [...columnOrder.value];
    const draggedIndex = newOrder.indexOf(draggedColumn.value);
    const targetIndex = newOrder.indexOf(targetColName);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
        // Remove dragged column from its current position
        const draggedCol = newOrder.splice(draggedIndex, 1)[0];
        
        // Calculate new insertion position
        // If dragging from right to left, insert at target position
        // If dragging from left to right, insert after target position
        const insertIndex = draggedIndex < targetIndex ? targetIndex : targetIndex;
        
        newOrder.splice(insertIndex, 0, draggedCol);
        
        columnOrder.value = newOrder;
    }
    
    draggedColumn.value = null;
    dragOverColumn.value = null;
};

const handleDragEnd = (): void => {
    draggedColumn.value = null;
    dragOverColumn.value = null;
};

// Search debouncing logic
const handleSearchInput = (): void => {
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
    }
    
    searchTimeout.value = setTimeout(() => {
        if (hasUpdateFunction.value) {
            // Server-side filtering: emit update to parent or call update function
            localFilter.value.page = 0; // Reset to first page when searching
            updateFilter();
        } else {
            // Client-side filtering: update local debounced search
            debouncedSearch.value = localFilter.value.search || '';
            localFilter.value.page = 0; // Reset to first page when searching
        }
    }, 300); // 300ms debounce delay
};

// Emit filter updates or call update function if provided
const updateFilter = (): void => {
    if (hasUpdateFunction.value) {
        if (props.updateFunction) {
            props.updateFunction({ ...localFilter.value });
        } else {
            emit('update', { ...localFilter.value });
        }
    }
};

// Cleanup
onUnmounted(() => {
    document.removeEventListener('click', handleColumnClickOutside);
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
    }
});
</script>

<style scoped>
.data-table-container {
    max-width: 100%;
    min-height: 400px;
    overflow: auto;
}

.table th {
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
    background: #dfdfdf;
}

.table td.position-sticky {
    background: white;
    z-index: 1;
}

.resizer {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 5px;
    cursor: col-resize;
    background: transparent;
}

.resizer:hover {
    background: #ccc;
}

.cursor-pointer {
    cursor: pointer;
}

/* Drag and drop styles */
.draggable-column {
    cursor: move;
    transition: all 0.2s ease;
}

.draggable-column.dragging {
    opacity: 0.5;
    transform: scale(0.95);
}

.draggable-column.drag-over {
    background-color: #e3f2fd !important;
    border-left: 3px solid #2196f3;
}

.drag-handle {
    color: #999;
    cursor: grab;
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.drag-handle:hover {
    opacity: 1;
    color: #666;
}

.draggable-column:hover .drag-handle {
    opacity: 1;
}

/* Fix dropdown hover text color - don't use white on hover */
:deep(.dropdown-menu .dropdown-item:hover),
:deep(.dropdown-menu .dropdown-item:focus) {
    color: #1e2125;
    background-color: #e9ecef;
}

:deep(.dropdown-menu .dropdown-item:active) {
    color: #1e2125;
    background-color: #dee2e6;
}

/* Column selector styling - match autoselect appearance */
.column-selector {
  display: inline-block;
  position: relative;
}

.column-selector-trigger {
  background: transparent;
  border: 1px solid transparent;
  color: #0d6efd;
  cursor: pointer;
  text-decoration: none;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.column-selector-trigger:hover {
  color: #0a58ca;
  background-color: #f8f9fa;
  border-color: #dee2e6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.column-selector-trigger:active,
.column-selector-trigger:focus {
  background-color: #e9ecef;
  border-color: #ced4da;
  outline: none;
}

.column-selector-trigger .trigger-label {
  font-weight: 500;
}

.column-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
  min-width: 220px;
  margin-top: 0.125rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-height: 400px;
  overflow-y: auto;
}

.column-dropdown .dropdown-item {
  cursor: pointer;
  padding: 0.5rem 1rem;
}

.column-dropdown .dropdown-item label {
  cursor: pointer;
  width: 100%;
}

.column-select-all-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.column-select-all-header:hover {
  background-color: #e9ecef;
}
</style>